import { Dialog, DialogTitle, TextField } from '@mui/material';
import { type FormEvent, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SAVED_FILTER_NAME_MAX_LENGTH } from '~/data/savedFilter';

import { Button, Icon, IconButton } from '@vestigas/ui';

import {
  SavedFilterDeleteConfirmationDialog,
  type SavedFilterDeleteAction,
} from './SavedFilterDeleteConfirmationDialog';

type SavedFilterNameDialogProps = {
  readonly deleteAction?: SavedFilterDeleteAction;
  readonly initialName?: string;
  readonly isOpen: boolean;
  readonly isPending: boolean;
  readonly submitText: string;
  readonly onClose: () => void;
  readonly onSubmit: (name: string) => void;
};

export function SavedFilterNameDialog({
  deleteAction,
  initialName = '',
  isOpen,
  isPending,
  submitText,
  onClose,
  onSubmit,
}: SavedFilterNameDialogProps) {
  const { t } = useTranslation('universalFilters');
  const nameInputId = useId();
  const [name, setName] = useState(initialName);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const isBusy = isPending || Boolean(deleteAction?.isPending);
  const isNameMaxLengthMessageVisible =
    name.length >= SAVED_FILTER_NAME_MAX_LENGTH;

  useEffect(() => {
    if (isOpen) {
      setName(initialName.slice(0, SAVED_FILTER_NAME_MAX_LENGTH));
    }
  }, [initialName, isOpen]);

  useEffect(() => {
    if (!isOpen) setIsDeleteConfirmationOpen(false);
  }, [isOpen]);

  function handleClose() {
    if (!isBusy) onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmedName = name.trim();
    if (!trimmedName || isBusy) return;

    onSubmit(trimmedName);
  }

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={isOpen}
        slotProps={{
          paper: {
            className: 'rounded-md p-0',
          },
        }}
        onClose={handleClose}
      >
        <form className="flex flex-col gap-6 p-6" onSubmit={handleSubmit}>
          <header className="flex items-start justify-between gap-4">
            <DialogTitle className="text-tiga-text p-0 text-xl font-semibold">
              {t(($) => $.savedFilters.nameDialog.title)}
            </DialogTitle>
            <IconButton
              aria-label={t(($) => $.savedFilters.nameDialog.close.ariaLabel)}
              className="-mt-1 -mr-1"
              disabled={isBusy}
              size="sm"
              type="button"
              onClick={handleClose}
            >
              <Icon metaphor="close" className="size-5" />
            </IconButton>
          </header>

          <div className="flex flex-col gap-2">
            <label
              className="text-tiga-text text-sm font-medium"
              htmlFor={nameInputId}
            >
              {t(($) => $.savedFilters.nameDialog.name.label)} *
            </label>
            <TextField
              autoFocus
              fullWidth
              disabled={isBusy}
              helperText={
                isNameMaxLengthMessageVisible
                  ? t(($) => $.savedFilters.nameMaxLengthMessage, {
                      maxLength: SAVED_FILTER_NAME_MAX_LENGTH,
                    })
                  : ' '
              }
              id={nameInputId}
              placeholder={t(($) => $.savedFilters.nameDialog.name.placeholder)}
              size="small"
              slotProps={{
                htmlInput: { maxLength: SAVED_FILTER_NAME_MAX_LENGTH },
              }}
              value={name}
              onChange={(event) => {
                const nextName = event.target.value;
                setName(nextName.slice(0, SAVED_FILTER_NAME_MAX_LENGTH));
              }}
            />
          </div>

          <footer className="flex items-center gap-3">
            {deleteAction ? (
              <Button
                buttonStyle="link"
                className="mr-auto px-0 text-tiga-action-default"
                disabled={isBusy}
                icon={<Icon metaphor="deleteOutline" size="inherit" />}
                text={deleteAction.text}
                type="button"
                onClick={() => {
                  setIsDeleteConfirmationOpen(true);
                }}
              />
            ) : null}
            <Button
              buttonStyle="primary"
              className="ml-auto"
              disabled={!name.trim() || isBusy}
              loading={isPending}
              text={submitText}
              type="submit"
            />
          </footer>
        </form>
      </Dialog>
      {deleteAction ? (
        <SavedFilterDeleteConfirmationDialog
          deleteAction={deleteAction}
          isOpen={isDeleteConfirmationOpen}
          onClose={() => {
            setIsDeleteConfirmationOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
